<template>
  <div class="flex flex-col">
    <!-- Режим просмотра: дата установлена, редактирование скрыто -->
    <template v-if="!isEditMode">
      <div class="flex items-center justify-between gap-3">
        <div class="text-sm text-muted-foreground">
          {{ $t("Срок действия установлен до") }}
          <div class="inline font-bold text-foreground">{{ savedEndDate }}</div>
        </div>
        <Button
          variant="outline"
          size="icon"
          @click="editing = true"
          class="w-8 h-8"
        >
          <Pencil class="size-4 text-foreground" />
        </Button>
      </div>
    </template>

    <!-- Режим редактирования -->
    <template v-else>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 py-2">
        <FormItem float>
          <FormLabel>{{ $t("Срок действия") }}</FormLabel>
          <FormControl>
            <Input v-model="amount" type="number" min="1" class="w-full" />
          </FormControl>
        </FormItem>

        <FormItem float>
          <FormLabel>{{ $t("Единица") }}</FormLabel>
          <FormControl>
            <Select v-model="unit">
              <SelectTrigger class="w-full">
                <SelectValue :placeholder="$t('Выберите единицу')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="u in availableUnits"
                  :key="u.code"
                  :value="u.code"
                >
                  {{ $t(u.label) }}
                </SelectItem>
              </SelectContent>
            </Select>
          </FormControl>
        </FormItem>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-3">
        <Button
          variant="default"
          class="gap-2"
          :disabled="!canSet || loading || adding"
          @click="onApply"
        >
          <Spinner v-if="loading || adding" size="sm" class="text-current" />
          <Check v-else-if="isAdded" class="size-4" />
          {{
            isAdded
              ? $t("Добавлено")
              : savedEndDate
              ? $t("Добавить в документ")
              : $t("Установить срок и добавить в документ")
          }}
        </Button>

        <div class="text-sm text-muted-foreground">
          {{ $t("Срок действия установлен до") }}
          <div class="inline font-bold text-foreground">{{ savedEndDate }}</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { Check, Pencil } from "lucide-vue-next";
import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import Button from "@/shared/ui/button/Button.vue";
import { Spinner } from "@/shared/ui/spinner";
import { useEndDateField } from "@/features/notarial-actions/composables/useEndDateField";

const props = defineProps({
  form: { type: Object, required: true },
});

const emit = defineEmits(["insert:field"]);

const {
  amount,
  unit,
  loading,
  adding,
  availableUnits,
  savedEndDate,
  isAdded,
  canSet,
  applyEndDate,
  addToDocument,
} = useEndDateField(props.form);

const editing = ref(false);
const isEditMode = computed(() => !savedEndDate.value || editing.value);
const canCancel = computed(() => !!savedEndDate.value && editing.value);

function cancelEdit() {
  editing.value = false;
}

async function onApply() {
  editing.value = true;
  await applyEndDate();
  await addToDocument((field) => emit("insert:field", field));
}

defineExpose({ savedEndDate, canCancel, cancelEdit });
</script>
